import { 
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Home,
  Users,
  Egg,
  MapPin,
  DollarSign,
  Shirt,
  Star,
  Crown,
  Gift,
  Settings,
  LogOut,
  Sparkles,
  Globe,
  MessageCircle,
  Award,
  MessagesSquare,
  Trophy,
  CalendarRange,
  Compass,
  History,
  Camera,
  Music,
  Sun,
  Bell,
  MapPinned,
  Briefcase,
  Search,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { getDisplayName, getInitials } from "@/lib/display-name";
import { getLocalTodayStr } from "@/lib/date-utils";
import { useQuery } from "@tanstack/react-query";

function getTierKey(tier: string | null | undefined): string {
  switch (tier) {
    case "aventurero": return "sidebar.tierAventurero";
    case "globetrotter": return "sidebar.tierGlobetrotter";
    default: return "sidebar.tierExplorer";
  }
}

export function AppSidebar() {
  const [location] = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const { data: myTripsRaw } = useQuery<any>({ queryKey: ["/api/trips/mine"], enabled: !isMobile });
  const allTrips = Array.isArray(myTripsRaw)
    ? myTripsRaw
    : [...(myTripsRaw?.created || []), ...(myTripsRaw?.joined || []), ...(myTripsRaw?.troop || [])];

  const homeItem = { title: t('sidebar.dashboard'), url: "/dashboard", icon: Home };

  // Sidebar groups mirror the 4 BottomNav tabs (Explore / The Scene / Trips / Troop)
  // so the desktop and mobile mental models stay in sync.
  const exploreItems = [
    { title: t('sidebar.exploreFrontDoor', 'Explore'), url: "/explore", icon: Search },
    { title: t('sidebar.localVibes', 'The Scene'), url: "/the-scene", icon: MapPinned },
    { title: t('sidebar.festivalGuide', 'Festival Guide'), url: "/festival-guide", icon: Music },
    { title: t('sidebar.discoverTrips'), url: "/discover", icon: Globe },
    { title: t('sidebar.endeavor', 'Journeys'), url: "/endeavor", icon: Compass },
  ];

  const hasActiveTrip = allTrips.some((trip: any) => {
    if (!trip.startDate) return false;
    const today = getLocalTodayStr();
    const start = trip.startDate.split("T")[0];
    const end = (trip.endDate || trip.startDate).split("T")[0];
    if (today >= start && today <= end) return true;
    const startDate = new Date(start + "T00:00:00");
    const todayDate = new Date(today + "T00:00:00");
    const daysUntil = Math.round((startDate.getTime() - todayDate.getTime()) / 86400000);
    return daysUntil > 0 && daysUntil <= 14;
  });

  // Trips bucket = bottom-nav "Trips" tab targets (planning + active trip tools)
  const tripsItems = [
    { title: t('sidebar.travelCalendar'), url: "/travel-calendar", icon: CalendarRange },
    { title: t('sidebar.createTrip'), url: "/create-trip", icon: Sparkles },
    { title: t('sidebar.myTrips'), url: "/my-trips", icon: MapPin },
    ...(hasActiveTrip ? [{ title: t('sidebar.dailyGuide', 'Daily Guide'), url: "/daily-guide", icon: Sun }] : []),
    { title: t('sidebar.expenses'), url: "/expenses", icon: DollarSign },
    { title: t('sidebar.packingAssistant'), url: "/packing", icon: Shirt },
  ];

  // Troop bucket = bottom-nav "Troop" tab targets (people + social)
  const troopItems = [
    { title: t('sidebar.creature', 'Troopie'), url: "/creature", icon: Egg },
    { title: t('sidebar.myTroop', 'My Troop'), url: "/friends", icon: Users },
    { title: t('sidebar.messages'), url: "/messages", icon: MessageCircle },
    { title: t('sidebar.tripPhotos'), url: "/photos", icon: Camera },
    { title: t('sidebar.communityBoard'), url: "/community", icon: MessagesSquare },
    { title: t('sidebar.reviews'), url: "/reviews", icon: Award },
    { title: t('sidebar.referrals'), url: "/referrals", icon: Gift },
  ];

  const resourceItems = [
    { title: t('sidebar.pointsMiles'), url: "/points-miles", icon: Star },
  ];


  const renderMenuItems = (items: { title: string; url: string; icon: any }[]) => (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.url}>
          <SidebarMenuButton asChild isActive={location === item.url}>
            <Link href={item.url} onClick={closeMobileSidebar}>
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );

  return (
    <Sidebar aria-label="Sidebar navigation">
      <SidebarHeader className="p-4">
        <Link href={user ? `/profile/${user.id}` : "#"} onClick={closeMobileSidebar}>
          <div className="flex items-center gap-3 hover-elevate rounded-md p-1 -m-1" data-testid="sidebar-user-profile">
            <Avatar>
              {user?.profileImageUrl && (
                <AvatarImage src={user.profileImageUrl} alt={user.firstName || "Profile"} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground">
                {getInitials(user)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate" data-testid="text-sidebar-username">
                {getDisplayName(user)}
              </div>
              <div className="text-xs text-muted-foreground" data-testid="text-sidebar-tier">
                {t(getTierKey(user?.premiumTier))} {t('sidebar.member')}
              </div>
            </div>
          </div>
        </Link>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === homeItem.url}>
                  <Link href={homeItem.url} onClick={closeMobileSidebar}>
                    <homeItem.icon className="h-4 w-4" />
                    <span>{homeItem.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === "/notifications"}>
                  <Link href="/notifications" onClick={closeMobileSidebar}>
                    <Bell className="h-4 w-4" />
                    <span>{t('sidebar.notifications', 'Notifications')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('bottomNav.explore', 'Explore')}</SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(exploreItems)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('bottomNav.trips', 'Trips')}</SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(tripsItems)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('bottomNav.social', 'Troop')}</SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(troopItems)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.resources')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {resourceItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={location === item.url}>
                    <Link href={item.url} onClick={closeMobileSidebar}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                      <Badge variant="outline" className="ml-auto text-[10px] border-amber-500 text-amber-600 dark:text-amber-400">{t('common.comingSoon', 'Coming Soon')}</Badge>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === "/premium"}>
                  <Link href="/premium" onClick={closeMobileSidebar}>
                    <Crown className="h-4 w-4" />
                    <span>{t('sidebar.premium')}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">{t('sidebar.upgrade')}</Badge>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location === "/settings"}>
              <Link href="/settings" onClick={closeMobileSidebar}>
                <Settings className="h-4 w-4" />
                <span>{t('sidebar.settings')}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location === "/transactions"}>
              <Link href="/transactions" onClick={closeMobileSidebar}>
                <History className="h-4 w-4" />
                <span>{t('sidebar.transactionHistory')}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => logout()}>
              <LogOut className="h-4 w-4" />
              <span>{t('sidebar.signOut')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
